// Approval Policy — who signs off a payroll run, per company (Phase 4/5b, D5–D8).
//
// A policy belongs to ONE company or is the company-group default that governs every
// company without its own. The company selector at the top picks which one is edited;
// GET /api/v1/approval-policy?legal_entity_id= returns the policy that GOVERNS that company
// and `source` says whether it is the company's own, the group default, or the built-in.
//
// The chain is the fixed vocabulary HR → Finance → Group Finance → Final. Finance is the
// locked floor (always required + distinct). Group Finance is an optional escalation that
// only an approver with access to every company can sign. Thresholds are in the company's
// base currency (read-only — no FX). Runs already submitted keep the policy they were
// submitted under; editing here changes future submissions only.
//
// The company-group default needs access to every company to change (the server answers
// 403 otherwise); after such a refusal the editor locks itself for the default and says why
// instead of letting the admin keep editing something they cannot save.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Loader2,
  Save,
  Lock,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Plus,
  Trash2,
  Undo2,
  Building2,
  Info,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Switch } from '@/features/shared/components/ui/switch';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import {
  getApprovalPolicy,
  setApprovalPolicy,
  deleteApprovalPolicy,
  listApprovalPolicies,
  listLegalEntities,
  getEmployeePortalSettings,
  setEmployeePortalSettings,
  type ApprovalLevelKey,
  type ApprovalPolicy,
  type EmployeePortalSettings,
  type LegalEntity,
} from '@/services/payrollSettingsService';
import { getRoles, type Role } from '@/services/rbacService';
import { getApiErrorMessage } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import {
  LEVEL_HINT,
  LEVEL_LABEL,
  addLevel,
  buildLevels,
  firstUnboundLevel,
  hasLevel,
  patchRow,
  policyStatus,
  removeLevel,
  seedRows,
  type LevelRow,
} from '../lib/approvalPolicyDraft';

const fieldCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60 disabled:cursor-not-allowed';
const labelCls = 'text-xs font-medium text-muted-foreground';

/** '' = the company-group default. */
type Selection = string;
const GROUP_DEFAULT: Selection = '';

const httpStatus = (e: unknown) => (e as { response?: { status?: number } })?.response?.status;

// A native role <select> matching the rest of Payroll Settings. If the level's
// current role_key isn't a tenant role (e.g. a payroll permission the server
// defaulted Finance to), we surface it as an explicit option so the value still shows.
const RoleSelect: React.FC<{
  value: string;
  roles: Role[];
  disabled?: boolean;
  onChange: (v: string) => void;
  id?: string;
}> = ({ value, roles, disabled, onChange, id }) => {
  const known = roles.some((r) => r.key === value);
  return (
    <select id={id} className={fieldCls} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select a role…</option>
      {roles.map((r) => (
        <option key={r.key} value={r.key}>
          {r.label}
        </option>
      ))}
      {value && !known && <option value={value}>{value}</option>}
    </select>
  );
};

const toneCls: Record<'info' | 'success' | 'neutral', string> = {
  info: 'bg-info-soft text-info-strong',
  success: 'bg-success-soft text-success-strong',
  neutral: 'border border-border bg-card text-foreground',
};

type PolicyLoad =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; policy: ApprovalPolicy };

export const ApprovalPolicyTab: React.FC = () => {
  const confirm = useConfirm();

  // Companies in reach (the legal-entities list is reach-filtered server-side).
  const [entities, setEntities] = useState<LegalEntity[] | null>(null);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);
  // Which companies have their own policy (from GET /approval-policies). null = unknown.
  const [ownPolicyIds, setOwnPolicyIds] = useState<Set<string> | null>(null);
  const [selection, setSelection] = useState<Selection>(GROUP_DEFAULT);

  const [load, setLoad] = useState<PolicyLoad>({ status: 'loading' });
  const [rows, setRows] = useState<LevelRow[]>([]);
  const [distinctFinal, setDistinctFinal] = useState(false);
  const [distinctReleaser, setDistinctReleaser] = useState(false);
  const [releaserNotApprover, setReleaserNotApprover] = useState(false);

  // Set after the server refused a change to the group default (403): the message it gave.
  const [defaultLocked, setDefaultLocked] = useState<string | null>(null);

  const [roles, setRoles] = useState<Role[]>([]);
  const [portal, setPortal] = useState<EmployeePortalSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const forCompany = selection !== GROUP_DEFAULT;
  const entity = useMemo(() => entities?.find((e) => e.id === selection) ?? null, [entities, selection]);
  const locked = !forCompany && !!defaultLocked;

  // ---- one-off loads: companies, which have their own policy, roles, portal ----------
  const refreshOwnPolicies = useCallback(() => {
    listApprovalPolicies()
      .then((ps) =>
        setOwnPolicyIds(new Set(ps.filter((p) => p.source === 'entity' && p.legal_entity_id).map((p) => p.legal_entity_id!))),
      )
      .catch(() => setOwnPolicyIds(null));
  }, []);

  useEffect(() => {
    let alive = true;
    listLegalEntities()
      .then((es) => alive && setEntities(es))
      .catch((e) => {
        if (!alive) return;
        setEntities([]);
        setEntitiesError(getApiErrorMessage(e, 'Your companies could not be loaded.'));
      });
    refreshOwnPolicies();
    getRoles()
      .then((r) => alive && setRoles([...r.systemRoles, ...r.customRoles]))
      .catch(() => {});
    getEmployeePortalSettings()
      .then((p) => alive && setPortal(p))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [refreshOwnPolicies]);

  // ---- the selected policy ------------------------------------------------------------
  const applyPolicy = (p: ApprovalPolicy) => {
    setRows(seedRows(p.approval_levels ?? p.effective_levels));
    setDistinctFinal(!!p.require_distinct_final_approver);
    setDistinctReleaser(!!p.require_distinct_releaser);
    setReleaserNotApprover(!!p.require_releaser_not_approver);
    setLoad({ status: 'ready', policy: p });
  };

  const loadPolicy = useCallback(async (sel: Selection) => {
    setLoad({ status: 'loading' });
    setFormError(null);
    try {
      applyPolicy(await getApprovalPolicy(sel || null));
    } catch (e) {
      setLoad({
        status: 'error',
        message:
          httpStatus(e) === 404
            ? 'This company is not available to you.'
            : getApiErrorMessage(e, 'The approval policy could not be loaded.'),
      });
    }
  }, []);

  useEffect(() => {
    void loadPolicy(selection);
  }, [selection, loadPolicy]);

  const policy = load.status === 'ready' ? load.policy : null;
  const baseCurrency = forCompany ? policy?.base_currency || entity?.base_currency || '' : '';
  const status = policy ? policyStatus(policy, forCompany) : null;

  const chainPreview = useMemo(() => {
    const keys: ApprovalLevelKey[] = [];
    rows.forEach((r) => {
      if (!keys.includes(r.key)) keys.push(r.key);
    });
    return keys;
  }, [rows]);

  const refuseDefault = (e: unknown): boolean => {
    if (!forCompany && httpStatus(e) === 403) {
      setDefaultLocked(
        getApiErrorMessage(
          e,
          'The company-group default applies to every company, so changing it needs access to all of them.',
        ),
      );
      return true;
    }
    return false;
  };

  // ---- save ------------------------------------------------------------------------------
  const save = async () => {
    if (!policy || locked) return;
    setFormError(null);
    const unbound = firstUnboundLevel(rows);
    if (unbound) {
      setFormError(`Choose a role for the ${LEVEL_LABEL[unbound.key]} level before saving.`);
      return;
    }
    setSaving(true);
    try {
      const next = await setApprovalPolicy({
        legal_entity_id: forCompany ? selection : null,
        require_distinct_final_approver: distinctFinal,
        require_distinct_releaser: distinctReleaser,
        require_releaser_not_approver: releaserNotApprover,
        approval_levels: buildLevels(rows, baseCurrency || null),
      });
      applyPolicy({ ...next, base_currency: next.base_currency ?? policy.base_currency });
      if (!forCompany && portal) setPortal(await setEmployeePortalSettings(portal));
      refreshOwnPolicies();
      setSaved('Approval policy saved');
      setTimeout(() => setSaved(null), 3000);
    } catch (e) {
      if (refuseDefault(e)) return;
      // 400 validation messages are the server's own words — shown verbatim.
      toast.error(getApiErrorMessage(e, 'Could not save the approval policy.'), { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  // ---- revert ----------------------------------------------------------------------------
  const canRevert = !!policy && (forCompany ? policy.source === 'entity' : policy.source === 'tenant' && policy.configured);
  const revert = async () => {
    if (!canRevert || locked) return;
    const ok = await confirm({
      title: forCompany ? 'Revert to the group default?' : 'Revert to the built-in default?',
      message: forCompany
        ? `${entity?.name ?? 'This company'} will stop using its own approval chain and follow the company-group default again. Runs already submitted keep the policy they were submitted under.`
        : 'The company-group default will be removed, so every company without its own chain follows the built-in HR → Finance → Final chain. Runs already submitted keep the policy they were submitted under.',
      confirmLabel: forCompany ? 'Revert to group default' : 'Revert to built-in',
      danger: true,
    });
    if (!ok) return;
    setSaving(true);
    try {
      await deleteApprovalPolicy(forCompany ? selection : null);
      refreshOwnPolicies();
      await loadPolicy(selection);
      toast.success(forCompany ? 'Now using the company-group default' : 'Now using the built-in default');
    } catch (e) {
      if (refuseDefault(e)) return;
      toast.error(getApiErrorMessage(e, 'Could not revert the approval policy.'), { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const companyLabel = (e: LegalEntity) => {
    if (!ownPolicyIds) return e.name;
    return ownPolicyIds.has(e.id) ? `${e.name} — custom` : `${e.name} — group default`;
  };

  // ---- render ----------------------------------------------------------------------------
  return (
    <div className="space-y-4">
      {/* Which policy ---------------------------------------------------------- */}
      <Card>
        <CardContent className="max-w-3xl space-y-4 p-6">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-charcoal dark:text-foreground">
              <Building2 className="h-4 w-4 text-primary-500" /> Which company
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Each company can have its own approval chain. A company without one follows the company-group default.
            </p>
          </div>
          <label className="block max-w-md space-y-1.5" htmlFor="policy-company">
            <span className={labelCls}>Company</span>
            <select
              id="policy-company"
              className={fieldCls}
              value={selection}
              disabled={saving}
              onChange={(e) => setSelection(e.target.value)}
            >
              <option value={GROUP_DEFAULT}>Company-group default (all companies)</option>
              {(entities ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {companyLabel(e)}
                </option>
              ))}
            </select>
          </label>
          {entitiesError && (
            <p className="flex items-start gap-2 rounded-md bg-warning-soft px-3 py-2 text-xs text-warning-strong">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {entitiesError} Only the company-group default can be shown until they load.
            </p>
          )}

          {status && (
            <div className={cn('rounded-md px-3 py-2 text-sm', toneCls[status.tone])} data-testid="policy-source">
              <p className="font-medium">{status.label}</p>
              <p className="mt-0.5 text-xs opacity-90">{status.detail}</p>
            </div>
          )}

          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Runs keep the policy they were submitted under — changes here apply to runs submitted for approval from now on.
          </p>

          {locked && (
            <div role="alert" className="flex items-start gap-2 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning-strong">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">You can't change the company-group default</p>
                <p className="mt-0.5 text-xs">{defaultLocked} You can still give a company in your reach its own chain.</p>
              </div>
            </div>
          )}

          {canRevert && !locked && (
            <Button variant="outline" size="sm" className="gap-2" disabled={saving} onClick={() => void revert()}>
              <Undo2 className="h-4 w-4" />
              {forCompany ? 'Revert to group default' : 'Revert to built-in default'}
            </Button>
          )}
        </CardContent>
      </Card>

      {load.status === 'loading' && (
        <div className="py-12 text-center text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading approval policy…
        </div>
      )}

      {load.status === 'error' && (
        <div role="alert" className="rounded-lg bg-danger-soft p-4 text-sm text-danger-strong">
          <p className="font-medium">The approval policy could not be loaded</p>
          <p className="mt-0.5">{load.message}</p>
          <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => void loadPolicy(selection)}>
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
        </div>
      )}

      {load.status === 'ready' && (
        <fieldset disabled={locked || saving} className="space-y-4">
          <Card>
            <CardContent className="max-w-3xl space-y-6 p-6">
              <div>
                <h3 className="flex items-center gap-2 text-base font-semibold text-charcoal dark:text-foreground">
                  <ShieldCheck className="h-4 w-4 text-primary-500" /> Approval chain
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Who signs off a payroll run, and in what order. Whoever <strong>prepares</strong> a run can never
                  approve their own — and <strong>Finance</strong> is always a distinct second person. Add Group Finance
                  to escalate large runs to someone with access to every company.
                </p>
              </div>

              {/* Chain at a glance */}
              <div className="flex flex-wrap items-center gap-2" aria-label="Chain order">
                {chainPreview.map((key, i) => (
                  <React.Fragment key={key}>
                    {i > 0 && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium',
                        key === 'finance'
                          ? 'bg-primary-500/10 text-primary-500'
                          : key === 'group'
                            ? 'bg-info-soft text-info-strong'
                            : 'border border-border bg-card text-foreground',
                      )}
                    >
                      {key === 'finance' && <Lock className="h-3 w-3" />}
                      {LEVEL_LABEL[key]}
                    </span>
                  </React.Fragment>
                ))}
              </div>

              {/* Levels */}
              <div className="space-y-4">
                {rows.map((row) => (
                  <LevelEditor
                    key={row.rowId}
                    row={row}
                    roles={roles}
                    currencyLabel={forCompany ? baseCurrency || '—' : row.currency || "Each company's currency"}
                    onPatch={(patch) => setRows((cur) => patchRow(cur, row.rowId, patch))}
                    onRemove={() => setRows((cur) => removeLevel(cur, row.rowId))}
                  />
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                {(['hr', 'group', 'final'] as ApprovalLevelKey[])
                  .filter((k) => !hasLevel(rows, k))
                  .map((k) => (
                    <Button
                      key={k}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => setRows((cur) => addLevel(cur, k))}
                    >
                      <Plus className="h-4 w-4" />
                      Add {LEVEL_LABEL[k]}
                    </Button>
                  ))}
              </div>
            </CardContent>
          </Card>

          {/* Segregation of duties (same policy body) */}
          <Card>
            <CardContent className="max-w-3xl space-y-6 p-6">
              <div>
                <h3 className="text-base font-semibold text-charcoal dark:text-foreground">Segregation of duties</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Extra separation on the last steps. Turn these on for stricter control on larger teams.
                </p>
              </div>

              <ToggleRow
                title="Require a separate final sign-off"
                body="When on, the final sign-off must be a third person — different from whoever approved HR or Finance."
                checked={distinctFinal}
                onChange={setDistinctFinal}
              />
              <ToggleRow
                title="Require a separate payment release"
                body="When on, the person who releases payment under Disbursements must be different from whoever prepared the batch."
                checked={distinctReleaser}
                onChange={setDistinctReleaser}
              />
              <ToggleRow
                title="The person releasing payment must not be one of the approvers"
                body="When on, nobody who approved the run (or prepared it) can release its payment or record it as paid — a different person must."
                checked={releaserNotApprover}
                onChange={setReleaserNotApprover}
              />

              {!forCompany && portal && (
                <>
                  <div className="pt-2">
                    <h3 className="text-base font-semibold text-charcoal dark:text-foreground">Employee portal</h3>
                    <p className="mt-1 text-sm text-muted-foreground">What employees can see about their own pay under My Pay.</p>
                  </div>
                  <ToggleRow
                    title="Show base compensation to employees"
                    body="The My Pay Compensation tab: an employee's own base pay and its effective-dated history. Employees always see their base pay on payslips; turning this off only hides the tab."
                    checked={portal.show_compensation}
                    onChange={(v) => setPortal((cur) => (cur ? { ...cur, show_compensation: v } : cur))}
                  />
                </>
              )}

              {formError && <p className="text-sm text-danger-strong">{formError}</p>}

              <div className="flex items-center gap-3 pt-1">
                <Button onClick={() => void save()} disabled={saving || locked} className="gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {forCompany ? 'Save for this company' : 'Save group default'}
                </Button>
                {saved && (
                  <span className="flex items-center gap-1.5 text-sm text-success-strong">
                    <CheckCircle2 className="h-4 w-4" /> {saved}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </fieldset>
      )}
    </div>
  );
};

const ToggleRow: React.FC<{ title: string; body: string; checked: boolean; onChange: (v: boolean) => void }> = ({
  title,
  body,
  checked,
  onChange,
}) => (
  <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
    <div>
      <p className="text-sm font-medium text-charcoal dark:text-foreground">{title}</p>
      <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} aria-label={title} />
  </div>
);

const LevelEditor: React.FC<{
  row: LevelRow;
  roles: Role[];
  currencyLabel: string;
  onPatch: (patch: Partial<LevelRow>) => void;
  onRemove: () => void;
}> = ({ row, roles, currencyLabel, onPatch, onRemove }) => {
  const isFin = row.key === 'finance';
  const isGroup = row.key === 'group';
  const groupScoped = isGroup || row.scope === 'group';
  const id = row.rowId;
  return (
    <div className="rounded-lg border border-border p-4" data-testid={`level-${row.key}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-charcoal dark:text-foreground">
            {LEVEL_LABEL[row.key]}
            {isFin && (
              <span className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
                <Lock className="h-3 w-3" /> Always required
              </span>
            )}
            {groupScoped && (
              <span className="rounded bg-info-soft px-1.5 py-0.5 text-[10px] font-medium text-info-strong">Group Finance</span>
            )}
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">{LEVEL_HINT[row.key]}</p>
        </div>
        {isFin ? (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" /> Locked
          </span>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1.5 text-danger-strong hover:text-danger-strong"
            onClick={onRemove}
            aria-label={`Remove ${LEVEL_LABEL[row.key]}`}
          >
            <Trash2 className="h-4 w-4" /> Remove
          </Button>
        )}
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <label className="space-y-1.5" htmlFor={`role-${id}`}>
          <span className={labelCls}>Signed off by (role)</span>
          <RoleSelect id={`role-${id}`} value={row.role_key} roles={roles} onChange={(v) => onPatch({ role_key: v })} />
        </label>

        <div className="space-y-1.5">
          <span className={labelCls}>Approver</span>
          {isFin || isGroup ? (
            <p className="flex items-center gap-1.5 pt-2 text-sm text-muted-foreground">
              <Lock className="h-3.5 w-3.5" /> Must be a distinct person
            </p>
          ) : (
            <label className="flex items-center gap-2 pt-2 text-sm text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input"
                checked={row.distinct}
                onChange={(e) => onPatch({ distinct: e.target.checked })}
              />
              Must be a distinct person
            </label>
          )}
        </div>

        <label className="space-y-1.5" htmlFor={`scope-${id}`}>
          <span className={labelCls}>Who can sign</span>
          {isGroup ? (
            <input id={`scope-${id}`} className={fieldCls} value="Group Finance (access to every company)" disabled readOnly />
          ) : (
            <select
              id={`scope-${id}`}
              className={fieldCls}
              value={row.scope}
              onChange={(e) => {
                const scope = e.target.value === 'group' ? 'group' : 'entity';
                onPatch({ scope, substitute_allowed: scope === 'group' ? false : row.substitute_allowed });
              }}
            >
              <option value="entity">Someone with access to this company</option>
              <option value="group">Group Finance (access to every company)</option>
            </select>
          )}
        </label>

        <div className="space-y-1.5">
          <span className={labelCls}>Amount threshold (optional)</span>
          {isFin ? (
            <input className={fieldCls} value="Always applies" disabled readOnly aria-label="Finance threshold" />
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  id={`min-${id}`}
                  aria-label={`${LEVEL_LABEL[row.key]} threshold`}
                  type="number"
                  min={0}
                  step="0.01"
                  className={fieldCls}
                  placeholder="No threshold"
                  value={row.min_amount}
                  onChange={(e) => onPatch({ min_amount: e.target.value })}
                />
                <input
                  aria-label={`${LEVEL_LABEL[row.key]} threshold currency`}
                  className={cn(fieldCls, 'w-40 shrink-0')}
                  value={currencyLabel}
                  readOnly
                  disabled
                />
              </div>
              <span className="text-[11px] text-muted-foreground">
                Applies only when the run's net total is at or above this, in the company's own currency. Blank = always.
              </span>
            </>
          )}
        </div>

        <label className="space-y-1.5" htmlFor={`eff-${id}`}>
          <span className={labelCls}>Effective from (optional)</span>
          {isFin ? (
            <input className={fieldCls} value="Always in force" disabled readOnly />
          ) : (
            <input
              id={`eff-${id}`}
              type="date"
              className={fieldCls}
              value={row.effective_from}
              onChange={(e) => onPatch({ effective_from: e.target.value })}
            />
          )}
        </label>

        {!groupScoped && (
          <div className="space-y-1.5">
            <span className={labelCls}>Stand-in</span>
            <label className="flex items-center gap-2 pt-2 text-sm text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input"
                checked={row.substitute_allowed}
                onChange={(e) => onPatch({ substitute_allowed: e.target.checked })}
              />
              Group Finance may stand in
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalPolicyTab;
